import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, newId } from '../engine/context'
import { LOCAL_DB_SCHEMA } from '../schema'
import { binCode } from '../seed/wms-master-data.seed'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, idOf, parse } from './c07.shared'
import { stockFg } from './inventoryStock'
import {
  applyStatus,
  canWrite,
  type ImportCheck,
  type ImportRow,
  type ImportRowsBody,
  importFileOf,
  importRowsOf,
  importSummary,
  masterLog,
  myRoles,
  num,
  REASON_MIN,
  REASON_REQUIRED,
  registerEntity,
  text,
  type Usage,
  up,
  usageFromCounts,
  usageLabel
} from './masterData.shared'
import { binPart, binsOfRack, shapeFromPositions } from './rackShape'

/**
 * MST-06 Master Lokasi Gudang — `/api/v1/wms/master/lokasi/*`.
 * - Lokasi RM (WH-RM dan satelit WH-SGI): rack → bay → level → position (`mt_rack`, `mt_rack_bin`). Rack dibuat dan diubah
 *   lewat editor tampak depan (v7.50 M-02): bentuk tidak harus seragam; bay / level / posisi ditambah di ujung,
 *   dinonaktifkan (kode tetap), atau dihapus bila paling luar dan kosong. Satu baris per posisi tetap dipertahankan.
 * - Lokasi FG: zone penyimpanan (`warehouse_zone` STORAGE_FG) berisi pallet (`pallet` kind FG, kapasitas/rekomendasi per
 *   pallet) dan container Slop/Pack (`container`).
 * Aktif/nonaktif dan hapus permanen lewat endpoint umum `status`/`delete` (entitas `rack`, `fgzone`, `fgpallet`,
 * `container`). Isi rak sengaja tidak dihitung di master — itu urusan Stok Gabungan.
 */

const PAGE = 'MST-06'
/**
 * Batas bentuk rack. Keputusan user 2026-10-21 #7 (tutup E-6 / D85): TIDAK ada batas posisi per level untuk saat ini.
 * `position` hanya batas teknis format kode posisi dua angka (`R01-B01-L01-P99`), bukan aturan bisnis.
 */
export const POSITION_CODE_MAX = 99
export const RACK_LIMIT = { bay: 30, level: 8, position: POSITION_CODE_MAX }
const POSITION_LIMIT_TEXT = `Kode posisi memakai dua angka (P01–P${POSITION_CODE_MAX}); tidak ada batas posisi per level selain itu.`
/** Di atas jumlah lokasi ini, simpan rack meminta konfirmasi tambahan. */
export const RACK_LARGE = 200
export const PALLET_DEFAULT = { cap: 24, rec: 18 }
const PALLET_MAX_NEW = 12
const RACK_CODE = /^[RS]\d{2}$/
const ZONE_CODE = /^[A-Z][A-Z0-9-]{0,9}$/
const WH_LABEL: Record<string, string> = { 'WH-RM': 'WH RM', 'WH-SGI': 'WH Sansugi' }
const RM_STOCK_STATUS = 'IN_STOCK'
const MSG_RM = 'Menyunting lokasi RM hanya untuk Admin WH RM dan Admin WH Sansugi.'
const MSG_FG = 'Menyunting lokasi FG hanya untuk Admin WH FG dan Kadiv WH FG.'

const p2 = (value: number) => String(value).padStart(2, '0')
const digits = (value: unknown) => Number.parseInt(String(value ?? '').replace(/\D/g, ''), 10) || 0
const isInt = (value: unknown, min: number, max: number) => {
  const n = Number(value)
  return Number.isInteger(n) && n >= min && n <= max
}
const wibDate = (value: unknown) => {
  const raw = String(value ?? '')
  if (!raw) return null
  const d = new Date(new Date(raw).getTime() + 7 * 3600_000)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
  return `${p2(d.getUTCDate())} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return user ? [user.first_name, user.last_name].filter(Boolean).join(' ') || null : null
}

/* ------------------------------------------------------------------ hak & cakupan */

export type LocationScope = { rm: boolean; fg: boolean; whs: string[] }

/** Cakupan lokasi mengikuti peran (prototype `Sh.invScope`): FG hanya FG, Sansugi hanya WH-SGI, lainnya RM + satelit. */
export function locationScope(): LocationScope {
  const roles = myRoles()
  if (roles.some(code => code.endsWith('_fg') || code === 'sales_marketing'))
    return { rm: false, fg: true, whs: ['WH-FG'] }
  if (roles.includes('admin_wh_sansugi')) return { rm: true, fg: false, whs: ['WH-SGI'] }
  return { rm: true, fg: false, whs: ['WH-RM', 'WH-SGI'] }
}
const canWriteRm = () => canWrite(PAGE) && myRoles().some(code => code === 'admin_wh_rm' || code === 'admin_wh_sansugi')
const canWriteFg = () => canWrite(PAGE) && myRoles().some(code => code === 'admin_wh_fg' || code === 'kadiv_wh_fg')
const requireRm = () => {
  if (!canWriteRm()) fail(MSG_RM, 'ERR_UNAUTHORIZED_ROLE', 403)
}
const requireFg = () => {
  if (!canWriteFg()) fail(MSG_FG, 'ERR_UNAUTHORIZED_ROLE', 403)
}

/* ------------------------------------------------------------------ rack: model */

/**
 * Sebab nonaktif satu posisi (`mt_rack_bin.inactive_cause`, v7.50 M-02): `posisi` (dinonaktifkan sendiri), `level` / `bay`
 * (satu level / bay sekaligus), `rack` (seluruh rack), `manual` (nonaktif tanpa sebab tercatat — data lama / impor).
 * Saat diaktifkan kembali hanya sebab yang sama yang dibuka. `ukuran` (resize lewat angka, D40) dibaca sebagai `posisi`.
 */
export type OffCause = 'bay' | 'level' | 'posisi' | 'rack' | 'manual'
const OFF_CAUSES: OffCause[] = ['bay', 'level', 'posisi', 'rack', 'manual']
type Off = { cause: OffCause; reason: string } | null

type Position = {
  code: string
  b: number
  l: number
  p: number
  bin: LocalRow
  active: boolean
  cause: OffCause | ''
}

const zoneOfRack = (rack: LocalRow) => findRow('warehouse_zone', idOf(rack.zone_id))
const whCodeOfRack = (rack: LocalRow) =>
  String(findRow('mt_warehouses', idOf(zoneOfRack(rack)?.warehouse_id))?.code ?? 'WH-RM')
const binsOf = binsOfRack
const numOf = binPart

/** Sebab nonaktif yang tersimpan, dinormalkan (`ukuran` → `posisi`; nonaktif tanpa sebab → `manual`). */
export function causeOf(bin: LocalRow): OffCause | '' {
  if (bin.is_active !== false) return ''
  const raw = String(bin.inactive_cause ?? '')
  if (raw === 'ukuran') return 'posisi'
  return (OFF_CAUSES as string[]).includes(raw) ? (raw as OffCause) : 'manual'
}

/**
 * Bentuk rack TIDAK harus seragam (v7.50 M-02): `shape` = bay → level → jumlah posisi, mis. [[3,3,3],[3,3],[4,4,3]].
 * Datanya tetap satu baris per posisi (`mt_rack_bin`); bentuk diturunkan dari kode B/L/P.
 */
function shapeOf(rack: LocalRow) {
  const positions: Position[] = binsOf(rack.id)
    .map(bin => ({
      code: String(bin.code),
      b: numOf(bin, 'column'),
      l: numOf(bin, 'level'),
      p: numOf(bin, 'position'),
      bin,
      active: bin.is_active !== false,
      cause: causeOf(bin)
    }))
    .sort((a, b) => a.b - b.b || a.l - b.l || a.p - b.p)
  return { positions, ...shapeFromPositions(positions) }
}

/** "3" bila semua sama, "3–4" bila berbeda antar bay / level (prototype `JalRack.ringkas`). */
const spanText = (values: number[]) => {
  if (!values.length) return '0'
  const min = Math.min(...values)
  const max = Math.max(...values)
  return min === max ? String(max) : `${min}–${max}`
}
function shapeSummary(shape: number[][]) {
  const first = JSON.stringify(shape[0] ?? [])
  return {
    lv_text: spanText(shape.map(levels => levels.length)),
    pos_text: spanText(shape.flat()),
    uniform: shape.every(levels => JSON.stringify(levels) === first)
  }
}

/** Posisi yang berdiri stok RM (unit berlabel IN_STOCK). v7.50: tidak ada lagi stok awal tanpa label. */
function stockedBinIds(): Set<string> {
  const ids = new Set<string>()
  readTable('rm_unit').forEach(unit => {
    if (unit.unit_status === RM_STOCK_STATUS && unit.rack_bin_id) ids.add(idOf(unit.rack_bin_id))
  })
  return ids
}

/** Hitung referensi ke satu set id dari semua collection yang memiliki relasi ke `target` (kecuali jejak tx_log). */
export function referenceCounts(target: string, ids: Set<string>, exclude: string[] = []) {
  const counts = new Map<string, number>()
  if (!ids.size) return counts
  for (const [child, definition] of Object.entries(LOCAL_DB_SCHEMA)) {
    if (child === 'tx_log' || child === target || exclude.includes(child)) continue
    for (const [field, to] of Object.entries(definition.relations ?? {})) {
      if (to !== target) continue
      const hits = readTable(child).filter(row => ids.has(idOf(row[field]))).length
      if (hits) counts.set(child, (counts.get(child) ?? 0) + hits)
    }
  }
  return counts
}

/** Id posisi yang pernah dipakai data mana pun (stok, tugas, opname …) — riwayatnya harus tetap terbaca. */
function usedBinIds(ids: Set<string>): Set<string> {
  const used = new Set<string>()
  if (!ids.size) return used
  for (const [child, definition] of Object.entries(LOCAL_DB_SCHEMA)) {
    if (child === 'tx_log' || child === 'mt_rack_bin') continue
    for (const [field, to] of Object.entries(definition.relations ?? {})) {
      if (to !== 'mt_rack_bin') continue
      readTable(child).forEach(row => {
        const id = idOf(row[field])
        if (ids.has(id)) used.add(id)
      })
    }
  }
  return used
}

export const usageOfCounts = (counts: Map<string, number>): Usage => {
  const items = [...counts.entries()]
    .map(([collection, count]) => ({ collection, label: usageLabel(collection), count }))
    .sort((a, b) => b.count - a.count)
  return usageFromCounts(items.map(item => [item.label, item.count] as [string, number]))
}

const rackUsage = (rack: LocalRow): Usage =>
  usageOfCounts(referenceCounts('mt_rack_bin', new Set(binsOf(rack.id).map(bin => String(bin.id)))))

const categoryOf = (rack: LocalRow) => {
  const id = idOf(rack.category_restriction_id)
  const group = id ? findRow('mt_material_group', id) : undefined
  return group ? { id: String(group.id), name: String(group.name) } : null
}

function rackRow(rack: LocalRow) {
  const { positions, shape, ext } = shapeOf(rack)
  const wh = whCodeOfRack(rack)
  return {
    id: rack.id,
    code: String(rack.code),
    name: (rack.name as string | null) ?? null,
    wh,
    wh_label: WH_LABEL[wh] ?? wh,
    bay: ext.bay,
    lv: ext.lv,
    pos: ext.pos,
    ...shapeSummary(shape),
    locations: positions.filter(item => item.active).length,
    category: categoryOf(rack),
    is_active: rack.is_active !== false,
    inactive_reason: (rack.inactive_reason as string | null) ?? null
  }
}

/* ------------------------------------------------------------------ rack: editor bentuk (v7.50 M-02) */

/** Satu elemen yang dipilih di editor; nomor mulai 1. */
export type RackTarget = { kind: 'bay' | 'level' | 'position'; bay: number; level?: number; position?: number }
/** Perubahan bentuk: tambah hanya di ujung; hapus hanya yang paling luar dan kosong; nonaktif di mana saja (kode tetap). */
export type RackChange =
  | { op: 'add_bay' }
  | { op: 'add_level'; bay: number }
  | { op: 'add_position'; bay: number; level: number }
  | { op: 'deactivate'; target: RackTarget; reason: string }
  | { op: 'activate'; target: RackTarget }
  | { op: 'delete'; target: RackTarget }

type EdCell = { bin: LocalRow | null; off: Off; stocked: boolean; used: boolean }
type EdModel = EdCell[][][]
type EdScope = { b: number; l: number; p: number; cell: EdCell }

const OWN_CAUSE: Record<RackTarget['kind'], OffCause> = { bay: 'bay', level: 'level', position: 'posisi' }
const newCell = (off: Off = null): EdCell => ({ bin: null, off: off ? { ...off } : null, stocked: false, used: false })
const LEVEL_DEFAULT_POSITIONS = 3

function modelOf(rack: LocalRow): EdModel {
  const { positions, shape } = shapeOf(rack)
  const stocked = stockedBinIds()
  const used = usedBinIds(new Set(positions.map(item => String(item.bin.id))))
  const byCode = new Map(positions.map(item => [item.code, item]))
  return shape.map((levels, bi) =>
    levels.map((count, li) =>
      Array.from({ length: count }, (_, pi) => {
        const item = byCode.get(binCode(String(rack.code), bi + 1, li + 1, pi + 1))
        if (!item) return newCell()
        const id = String(item.bin.id)
        return {
          bin: item.bin,
          off: item.cause ? { cause: item.cause, reason: String(item.bin.inactive_reason ?? '') } : null,
          stocked: stocked.has(id),
          used: used.has(id)
        }
      })
    )
  )
}

const uniformModel = (bay: number, lv: number, pos: number): EdModel =>
  Array.from({ length: bay }, () => Array.from({ length: lv }, () => Array.from({ length: pos }, () => newCell())))

const targetName = (target: RackTarget) =>
  target.kind === 'bay'
    ? `Bay B${p2(target.bay)}`
    : target.kind === 'level'
      ? `Level L${p2(target.level ?? 0)} di B${p2(target.bay)}`
      : `Posisi B${p2(target.bay)}-L${p2(target.level ?? 0)}-P${p2(target.position ?? 0)}`

function scopeOf(model: EdModel, target: RackTarget): EdScope[] {
  const levels = model[target.bay - 1]
  if (!levels) fail(`Bay B${p2(target.bay)} tidak ada.`)
  if (target.kind !== 'bay' && !levels[(target.level ?? 0) - 1]) fail(`${targetName(target)} tidak ada.`)
  if (target.kind === 'position' && !levels[(target.level ?? 0) - 1]?.[(target.position ?? 0) - 1])
    fail(`${targetName(target)} tidak ada.`)
  const out: EdScope[] = []
  levels.forEach((cells, li) =>
    cells.forEach((cell, pi) => {
      if (target.kind !== 'bay' && li + 1 !== target.level) return
      if (target.kind === 'position' && pi + 1 !== target.position) return
      out.push({ b: target.bay, l: li + 1, p: pi + 1, cell })
    })
  )
  return out
}

/** Posisi / level baru ikut nonaktif bila seluruh tetangganya nonaktif karena level / bay yang sama. */
function inheritedOff(cells: EdCell[], bayOnly: boolean): Off {
  const first = cells[0]?.off
  if (!first || (first.cause !== 'bay' && (bayOnly || first.cause !== 'level'))) return null
  return cells.every(cell => cell.off?.cause === first.cause) ? first : null
}

const PINDAH_HINT = 'Pindahkan dulu lewat Pindah Material, lalu coba lagi.'

function applyChange(model: EdModel, change: RackChange) {
  const op = String((change as { op?: unknown })?.op ?? '')
  if (op === 'add_bay') {
    if (model.length >= RACK_LIMIT.bay) fail(`Satu rack paling banyak ${RACK_LIMIT.bay} bay.`)
    const last = model[model.length - 1] ?? [[newCell()]]
    model.push(last.map(cells => cells.map(() => newCell())))
    return
  }
  if (op === 'add_level') {
    const { bay } = change as { bay: number }
    const levels = model[bay - 1]
    if (!levels) fail(`Bay B${p2(bay)} tidak ada.`)
    if (levels.length >= RACK_LIMIT.level) fail(`Satu bay paling banyak ${RACK_LIMIT.level} level.`)
    const count = levels.length ? levels[levels.length - 1].length : LEVEL_DEFAULT_POSITIONS
    const off = inheritedOff(levels.flat(), true)
    levels.push(Array.from({ length: Math.min(count, RACK_LIMIT.position) }, () => newCell(off)))
    return
  }
  if (op === 'add_position') {
    const { bay, level } = change as { bay: number; level: number }
    const cells = model[bay - 1]?.[level - 1]
    if (!cells) fail(`Level L${p2(level)} di B${p2(bay)} tidak ada.`)
    if (cells.length >= RACK_LIMIT.position) fail(POSITION_LIMIT_TEXT)
    cells.push(newCell(inheritedOff(cells, false)))
    return
  }
  const target = (change as { target?: RackTarget }).target
  if (!target || !['bay', 'level', 'position'].includes(target.kind)) fail('Elemen rack yang dipilih tidak dikenal.')
  const scope = scopeOf(model, target)
  const own = OWN_CAUSE[target.kind]
  const name = targetName(target)
  if (op === 'deactivate') {
    const reason = text((change as { reason?: string }).reason)
    if (reason.length < REASON_MIN) fail(REASON_REQUIRED)
    const live = scope.filter(item => !item.cell.off)
    if (!live.length) fail(`${name} sudah nonaktif.`)
    const stocked = live.filter(item => item.cell.stocked).length
    if (stocked) fail(`Masih ada stok di ${stocked} lokasi pada ${name}. ${PINDAH_HINT}`, 'ERR_IN_USE', 409)
    live.forEach(item => {
      item.cell.off = { cause: own, reason }
    })
    return
  }
  if (op === 'activate') {
    const mine = scope.filter(
      item => item.cell.off?.cause === own || (own === 'posisi' && item.cell.off?.cause === 'manual')
    )
    if (!mine.length)
      fail(`${name} tidak dinonaktifkan sendiri — aktifkan dari bay / level / rack yang menonaktifkannya.`)
    mine.forEach(item => {
      item.cell.off = null
    })
    return
  }
  if (op === 'delete') {
    const stocked = scope.filter(item => item.cell.stocked).length
    if (stocked)
      fail(
        `Masih ada stok di ${stocked} lokasi, tidak bisa dihapus. ${PINDAH_HINT} Atau nonaktifkan saja.`,
        'ERR_IN_USE',
        409
      )
    const used = scope.filter(item => item.cell.used).length
    if (used)
      fail(
        `${used} lokasi pernah dipakai (ada riwayat stok atau tugas), tidak bisa dihapus supaya riwayatnya tetap terbaca. Nonaktifkan saja.`,
        'ERR_IN_USE',
        409
      )
    const levels = model[target.bay - 1]
    if (target.kind === 'position') {
      const cells = levels[(target.level ?? 0) - 1]
      if (cells.length <= 1) fail('Level butuh minimal 1 posisi. Hapus levelnya, atau nonaktifkan.')
      if (target.position !== cells.length)
        fail(
          `Hanya posisi paling ujung (P${p2(cells.length)}) yang bisa dihapus supaya kode lain tidak bergeser. Yang di tengah, nonaktifkan saja.`
        )
      cells.pop()
      return
    }
    if (target.kind === 'level') {
      if (levels.length <= 1) fail('Bay butuh minimal 1 level. Hapus bay-nya, atau nonaktifkan.')
      if (target.level !== levels.length)
        fail(`Hanya level paling atas (L${p2(levels.length)}) yang bisa dihapus. Yang di tengah, nonaktifkan saja.`)
      levels.pop()
      return
    }
    if (model.length <= 1) fail('Rack butuh minimal 1 bay. Nonaktifkan saja.')
    if (target.bay !== model.length)
      fail(`Hanya bay paling kanan (B${p2(model.length)}) yang bisa dihapus. Yang di tengah, nonaktifkan saja.`)
    model.pop()
    return
  }
  fail(`Perubahan "${op}" tidak dikenal.`)
}

function binRecord(rack: LocalRow, b: number, l: number, p: number, off: Off = null): LocalRow {
  return {
    ...auditFieldsForCreate(),
    id: newId(),
    rack_id: rack.id,
    code: binCode(String(rack.code), b, l, p),
    level: `L${p2(l)}`,
    column: `B${p2(b)}`,
    position: `P${p2(p)}`,
    is_active: !off,
    inactive_reason: off?.reason ?? null,
    inactive_cause: off?.cause ?? null,
    inactive_at: off ? new Date().toISOString() : null
  }
}

/* ------------------------------------------------------------------ rack: daftar & detail */

const racksInScope = (scope: LocationScope) =>
  readTable('mt_rack')
    .filter(rack => scope.whs.includes(whCodeOfRack(rack)))
    .sort((a, b) => {
      const byWh = whCodeOfRack(a).localeCompare(whCodeOfRack(b))
      return byWh || String(a.code).localeCompare(String(b.code))
    })

const categoryOptions = () =>
  readTable('mt_material_group')
    .slice()
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    .map(group => ({ id: String(group.id), name: String(group.name) }))

/** Penjelasan live isi pallet / container dari modul Stok Gabungan (tidak disimpan di master). */
function liveFg() {
  const stock = stockFg({})
  const pallets = new Map<string, ReturnType<typeof stockFg>['zones'][number]['pallets'][number]>()
  stock.zones.forEach(zone => zone.pallets.forEach(pallet => pallets.set(pallet.code, pallet)))
  const containers = new Map(stock.containers.map(container => [container.code, container]))
  return { pallets, containers }
}

const palletCap = (row: LocalRow) => num(row.capacity) || PALLET_DEFAULT.cap
const palletRec = (row: LocalRow) => num(row.recommended) || PALLET_DEFAULT.rec
const palletNo = (code: string) => Number.parseInt(code.split('-P')[1] ?? '0', 10) || 0

function palletRow(row: LocalRow, live: ReturnType<typeof liveFg>) {
  const stock = live.pallets.get(String(row.code))
  const empty = !stock || stock.units.length === 0
  return {
    id: row.id,
    code: String(row.code),
    no: palletNo(String(row.code)),
    cap: palletCap(row),
    rec: palletRec(row),
    sku_code: stock?.sku_code ?? null,
    unit_type: stock?.unit_type ?? null,
    fill: stock?.fill ?? 0,
    keq: stock?.keq ?? 0,
    count: stock?.count ?? 0,
    band: stock?.band ?? 'kosong',
    empty,
    is_active: row.is_active !== false,
    inactive_reason: (row.inactive_reason as string | null) ?? null
  }
}

function zoneRow(zone: LocalRow, live: ReturnType<typeof liveFg>) {
  const pallets = readTable('pallet')
    .filter(row => row.pallet_kind === 'FG' && idOf(row.zone_id) === zone.id)
    .sort((a, b) => palletNo(String(a.code)) - palletNo(String(b.code)))
    .map(row => palletRow(row, live))
  return {
    id: zone.id,
    code: String(zone.code),
    name: String(zone.name ?? zone.code),
    pallets,
    pallet_count: pallets.length,
    filled: pallets.filter(row => !row.empty).length,
    keq: Math.round(pallets.reduce((sum, row) => sum + row.keq, 0) * 100) / 100,
    capacity: pallets.reduce((sum, row) => sum + row.cap, 0),
    is_active: zone.is_active !== false,
    inactive_reason: (zone.inactive_reason as string | null) ?? null
  }
}

function containerRow(row: LocalRow, live: ReturnType<typeof liveFg>) {
  const stock = live.containers.get(String(row.code))
  return {
    id: row.id,
    code: String(row.code),
    name: String(row.name),
    kemasan: String(row.container_type) === 'Pack' ? 'Pack' : 'Slop',
    slop: stock?.slop ?? 0,
    pack: stock?.pack ?? 0,
    is_active: row.is_active !== false,
    inactive_reason: (row.inactive_reason as string | null) ?? null
  }
}

export function lokasiList() {
  const scope = locationScope()
  const racks = scope.rm ? racksInScope(scope).map(rackRow) : []
  const live = scope.fg ? liveFg() : null
  const zones =
    scope.fg && live
      ? readTable('warehouse_zone')
          .filter(zone => zone.zone_type === 'STORAGE_FG')
          .sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }))
          .map(zone => zoneRow(zone, live))
      : []
  const containers =
    scope.fg && live
      ? readTable('container')
          .sort((a, b) => String(a.code).localeCompare(String(b.code)))
          .map(row => containerRow(row, live))
      : []
  return {
    scope,
    can_rm: canWriteRm(),
    can_fg: canWriteFg(),
    limits: { ...RACK_LIMIT, large: RACK_LARGE },
    pallet_default: PALLET_DEFAULT,
    categories: scope.rm ? categoryOptions() : [],
    racks,
    rack_locations: racks.reduce((sum, row) => sum + row.locations, 0),
    zones,
    containers,
    pallet_total: zones.reduce((sum, zone) => sum + zone.pallet_count, 0)
  }
}

function rackById(id: unknown) {
  const rack = findRow('mt_rack', text(id))
  if (!rack) fail('Rack tidak ditemukan atau sudah tidak ada.', 'ERR_NOT_FOUND', 404)
  if (!locationScope().whs.includes(whCodeOfRack(rack)))
    fail('Rack ini di luar cakupan peran Anda.', 'ERR_UNAUTHORIZED_ROLE', 403)
  return rack
}

export function rackDetail(params?: { id?: string }) {
  const rack = rackById(params?.id)
  const { positions, shape, ext } = shapeOf(rack)
  const stocked = stockedBinIds()
  const used = usedBinIds(new Set(positions.map(item => String(item.bin.id))))
  const usage = rackUsage(rack)
  return {
    ...rackRow(rack),
    ext,
    shape,
    total_locations: positions.length,
    active_locations: positions.filter(item => item.active).length,
    created_at: wibDate(rack.date_created),
    created_by: userName(rack.user_created),
    used: usage.total > 0,
    usage,
    can_write: canWriteRm(),
    positions: positions.map(item => ({
      code: item.code,
      b: item.b,
      l: item.l,
      p: item.p,
      is_active: item.active,
      cause: item.cause || null,
      inactive_reason: item.active ? null : ((item.bin.inactive_reason as string | null) ?? null),
      has_stock: stocked.has(String(item.bin.id)),
      used: used.has(String(item.bin.id))
    }))
  }
}

/* ------------------------------------------------------------------ rack: simpan (editor bentuk) */

type RackEditBody = {
  id?: string
  wh?: string
  code?: string
  name?: string
  category_id?: string
  /** Rack baru saja: pola awal seragam (bay × level × posisi) sebelum `changes`. */
  pattern?: { bay?: unknown; lv?: unknown; pos?: unknown }
  changes?: RackChange[]
  confirm_large?: boolean
}

const rackWhZone = (wh: string) => {
  const warehouse = readTable('mt_warehouses').find(row => row.code === wh)
  const zone = readTable('warehouse_zone').find(
    row => idOf(row.warehouse_id) === warehouse?.id && row.zone_type === 'STORAGE'
  )
  if (!zone) fail(`Zona penyimpanan ${WH_LABEL[wh] ?? wh} belum ada.`, 'ERR_NOT_FOUND', 404)
  return zone
}

const rackExists = (code: string, exceptId?: string) =>
  readTable('mt_rack').find(row => String(row.code).toUpperCase() === code && row.id !== exceptId)

function validateRackCode(code: string, exceptId?: string) {
  if (!RACK_CODE.test(code)) fail('Format R + dua angka, mis. R04 (rack Sansugi memakai S + dua angka).')
  const clash = rackExists(code, exceptId)
  if (clash) fail(`Rack ${code} sudah ada di ${WH_LABEL[whCodeOfRack(clash)] ?? whCodeOfRack(clash)}.`)
}

function validatePattern(pattern: RackEditBody['pattern']) {
  if (!isInt(pattern?.bay, 1, RACK_LIMIT.bay)) fail(`Jumlah bay: isi 1 sampai ${RACK_LIMIT.bay}.`)
  if (!isInt(pattern?.lv, 1, RACK_LIMIT.level)) fail(`Jumlah level: isi 1 sampai ${RACK_LIMIT.level}.`)
  if (!isInt(pattern?.pos, 1, RACK_LIMIT.position))
    fail(`Posisi per level: bilangan bulat mulai 1. ${POSITION_LIMIT_TEXT}`)
  return uniformModel(Number(pattern?.bay), Number(pattern?.lv), Number(pattern?.pos))
}

const shapeText = (model: EdModel) =>
  model.map((levels, bi) => `B${p2(bi + 1)}: ${levels.map(cells => cells.length).join('/')}`).join(' · ')

type Diff = {
  created: { code: string; b: number; l: number; p: number; off: Off }[]
  deleted: LocalRow[]
  deactivated: { bin: LocalRow; off: NonNullable<Off> }[]
  reactivated: LocalRow[]
  reasonChanged: { bin: LocalRow; off: NonNullable<Off> }[]
  totalBefore: number
  totalAfter: number
}

/** Beda bentuk tersimpan dan model akhir, per kode posisi (kode tidak bergeser: tambah / hapus hanya di ujung). */
function diffOf(rackCode: string, before: LocalRow[], model: EdModel): Diff {
  const existing = new Map(before.map(bin => [String(bin.code), bin]))
  const seen = new Set<string>()
  const diff: Diff = {
    created: [],
    deleted: [],
    deactivated: [],
    reactivated: [],
    reasonChanged: [],
    totalBefore: before.length,
    totalAfter: 0
  }
  model.forEach((levels, bi) =>
    levels.forEach((cells, li) =>
      cells.forEach((cell, pi) => {
        const code = binCode(rackCode, bi + 1, li + 1, pi + 1)
        seen.add(code)
        diff.totalAfter += 1
        const bin = existing.get(code)
        if (!bin) {
          diff.created.push({ code, b: bi + 1, l: li + 1, p: pi + 1, off: cell.off })
          return
        }
        const was = causeOf(bin)
        if (cell.off && !was) diff.deactivated.push({ bin, off: cell.off })
        else if (!cell.off && was) diff.reactivated.push(bin)
        else if (cell.off && (cell.off.cause !== was || cell.off.reason !== String(bin.inactive_reason ?? '')))
          diff.reasonChanged.push({ bin, off: cell.off })
      })
    )
  )
  before.forEach(bin => {
    if (!seen.has(String(bin.code))) diff.deleted.push(bin)
  })
  return diff
}

const offPatch = (off: Off) => ({
  ...auditFieldsForUpdate(),
  is_active: !off,
  inactive_cause: off?.cause ?? null,
  inactive_reason: off?.reason ?? null,
  inactive_at: off ? new Date().toISOString() : null
})

/** Kelompok penonaktifan untuk log ("Nonaktifkan Level L03 di B02 rack R01 (3 lokasi) — alasan: …"), termasuk lokasi baru. */
function offGroups(items: { code: string; b: number; l: number; off: NonNullable<Off> }[]) {
  const groups = new Map<string, { name: string; reason: string; n: number }>()
  items.forEach(({ code, b, l, off }) => {
    const name =
      off.cause === 'bay' ? `Bay B${p2(b)}` : off.cause === 'level' ? `Level L${p2(l)} di B${p2(b)}` : `Posisi ${code}`
    const key = `${off.cause}|${name}|${off.reason}`
    const group = groups.get(key) ?? { name, reason: off.reason, n: 0 }
    group.n += 1
    groups.set(key, group)
  })
  return [...groups.values()]
}

const firstCodes = (codes: string[]) => `${codes.slice(0, 6).join(', ')}${codes.length > 6 ? ' …' : ''}`

/**
 * POST `rack/edit-structure` — Tambah Rack dan Ubah Rack lewat editor tampak depan (v7.50 M-02). Bentuk awal (rack
 * tersimpan, atau `pattern` seragam untuk rack baru) diberi `changes` satu per satu dengan aturan yang sama dengan
 * editor; semua divalidasi di memori sebelum ada yang ditulis. Menonaktifkan / menghapus yang berisi stok → 409
 * (Pindah Material); hapus hanya elemen paling luar yang kosong dan belum pernah dipakai.
 */
export function rackEditStructure(body: RackEditBody) {
  requireRm()
  const changes = Array.isArray(body.changes) ? body.changes : []
  const category = text(body.category_id)
  if (category && !findRow('mt_material_group', category)) fail('Kategori tidak dikenal.')
  const name = text(body.name) || null
  if (!body.id) {
    const scope = locationScope()
    const wh = text(body.wh) || scope.whs[0]
    if (!scope.whs.includes(wh)) fail('Gudang di luar cakupan peran Anda.', 'ERR_UNAUTHORIZED_ROLE', 403)
    const code = up(body.code)
    validateRackCode(code)
    const model = validatePattern(body.pattern)
    changes.forEach(change => applyChange(model, change))
    const total = model.flat(2).length
    if (total > RACK_LARGE && !body.confirm_large)
      fail(
        `Buat ${total} lokasi sekaligus? Jumlahnya besar (lebih dari ${RACK_LARGE}). Periksa lagi bentuknya sebelum melanjutkan.`,
        'ERR_CONFIRM_REQUIRED',
        409
      )
    const zone = rackWhZone(wh)
    const rack = insertRow('mt_rack', {
      ...auditFieldsForCreate(),
      id: newId(),
      code,
      name,
      zone_id: zone.id,
      category_restriction_id: category || null,
      is_active: true,
      inactive_reason: null
    }) as LocalRow
    let off = 0
    model.forEach((levels, bi) =>
      levels.forEach((cells, li) =>
        cells.forEach((cell, pi) => {
          if (cell.off) off += 1
          insertRow('mt_rack_bin', binRecord(rack, bi + 1, li + 1, pi + 1, cell.off))
        })
      )
    )
    masterLog(
      'RM',
      PAGE,
      'mst-add',
      code,
      `Tambah rack ${code} (${wh}) — ${model.length} bay, ${total} lokasi${off ? ` (${off} nonaktif)` : ''} (${shapeText(model)})`
    )
    return {
      id: rack.id,
      code,
      created: total,
      deleted: 0,
      deactivated: off,
      reactivated: 0,
      total_before: 0,
      total_after: total
    }
  }
  const rack = rackById(body.id)
  const before = binsOf(rack.id)
  const model = modelOf(rack)
  if (changes.length && rack.is_active === false)
    fail('Rack nonaktif: aktifkan dulu dari halaman Detail untuk mengubah bentuknya.', 'ERR_RACK_INACTIVE', 409)
  changes.forEach(change => applyChange(model, change))
  const used = rackUsage(rack).total > 0
  const nextCode = used ? String(rack.code) : up(body.code) || String(rack.code)
  if (nextCode !== rack.code) validateRackCode(nextCode, rack.id)
  const diff = diffOf(String(rack.code), before, model)
  diff.deleted.forEach(bin => deleteRow('mt_rack_bin', bin.id))
  diff.created.forEach(item => insertRow('mt_rack_bin', binRecord(rack, item.b, item.l, item.p, item.off)))
  diff.deactivated.forEach(item => updateRow('mt_rack_bin', item.bin.id, offPatch(item.off)))
  diff.reasonChanged.forEach(item => updateRow('mt_rack_bin', item.bin.id, offPatch(item.off)))
  diff.reactivated.forEach(bin => updateRow('mt_rack_bin', bin.id, offPatch(null)))
  updateRow('mt_rack', rack.id, { ...auditFieldsForUpdate(), name, category_restriction_id: category || null })
  if (nextCode !== rack.code) renameRack(rack, nextCode)
  const parts = [
    diff.created.length ? `${diff.created.length} lokasi baru` : '',
    diff.deleted.length ? `${diff.deleted.length} dihapus` : '',
    diff.deactivated.length ? `${diff.deactivated.length} dinonaktifkan` : '',
    diff.reactivated.length ? `${diff.reactivated.length} diaktifkan kembali` : ''
  ].filter(Boolean)
  const categoryName = category ? String(findRow('mt_material_group', category)?.name ?? '') : ''
  masterLog(
    'RM',
    PAGE,
    'mst-edit',
    nextCode,
    `Ubah rack ${rack.code}${nextCode !== rack.code ? ` → ${nextCode}` : ''} — ${categoryName ? `khusus ${categoryName}` : 'semua kategori'}${parts.length ? ` · ${parts.join(', ')}` : ''}`
  )
  offGroups([
    ...diff.deactivated.map(({ bin, off }) => ({
      code: binCode(nextCode, numOf(bin, 'column'), numOf(bin, 'level'), numOf(bin, 'position')),
      b: numOf(bin, 'column'),
      l: numOf(bin, 'level'),
      off
    })),
    ...diff.created.flatMap(item =>
      item.off ? [{ code: binCode(nextCode, item.b, item.l, item.p), b: item.b, l: item.l, off: item.off }] : []
    )
  ]).forEach(group =>
    masterLog(
      'RM',
      PAGE,
      'mst-deactivate',
      nextCode,
      `Nonaktifkan ${group.name} rack ${nextCode} (${group.n} lokasi) — alasan: ${group.reason}`
    )
  )
  if (diff.reactivated.length)
    masterLog(
      'RM',
      PAGE,
      'mst-activate',
      nextCode,
      `Aktifkan Kembali ${diff.reactivated.length} lokasi rack ${nextCode}: ${firstCodes(diff.reactivated.map(bin => String(bin.code)))}`
    )
  if (diff.deleted.length)
    masterLog(
      'RM',
      PAGE,
      'mst-delete',
      nextCode,
      `Hapus ${diff.deleted.length} lokasi rack ${nextCode} (kosong, belum pernah dipakai): ${firstCodes(diff.deleted.map(bin => String(bin.code)))}`
    )
  return {
    id: rack.id,
    code: nextCode,
    created: diff.created.length,
    deleted: diff.deleted.length,
    deactivated: diff.deactivated.length,
    reactivated: diff.reactivated.length,
    total_before: diff.totalBefore,
    total_after: diff.totalAfter
  }
}

function renameRack(rack: LocalRow, code: string) {
  readTable('mt_rack_bin')
    .filter(bin => idOf(bin.rack_id) === rack.id)
    .forEach(bin => {
      updateRow('mt_rack_bin', bin.id, {
        ...auditFieldsForUpdate(),
        code: binCode(code, numOf(bin, 'column'), numOf(bin, 'level'), numOf(bin, 'position'))
      })
    })
  updateRow('mt_rack', rack.id, { ...auditFieldsForUpdate(), code })
}

/* ------------------------------------------------------------------ rack: posisi, status, hapus */

export function positionStatus(body: { rack_id?: string; code?: string; is_active?: boolean; reason?: string }) {
  requireRm()
  const rack = rackById(body.rack_id)
  if (rack.is_active === false) fail('Rack nonaktif. Aktifkan rack dari halaman Detail untuk mengubah posisinya.')
  const item = shapeOf(rack).positions.find(position => position.code === text(body.code))
  if (!item) fail('Posisi tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (body.is_active) {
    if (item.cause && item.cause !== 'posisi' && item.cause !== 'manual')
      fail(
        `Posisi ini nonaktif bersama ${item.cause === 'bay' ? `bay B${p2(item.b)}` : `level L${p2(item.l)} di B${p2(item.b)}`}. Aktifkan lewat tombol Ubah rack.`
      )
    updateRow('mt_rack_bin', item.bin.id, {
      ...auditFieldsForUpdate(),
      is_active: true,
      inactive_cause: null,
      inactive_reason: null,
      inactive_at: null
    })
    masterLog('RM', PAGE, 'mst-activate', item.code, `Aktifkan Kembali posisi ${item.code} (${whCodeOfRack(rack)})`)
    return { code: item.code, is_active: true }
  }
  const reason = text(body.reason)
  if (reason.length < REASON_MIN) fail(REASON_REQUIRED)
  if (stockedBinIds().has(String(item.bin.id))) fail(`Masih ada stok di posisi ini. ${PINDAH_HINT}`, 'ERR_IN_USE', 409)
  updateRow('mt_rack_bin', item.bin.id, {
    ...auditFieldsForUpdate(),
    is_active: false,
    inactive_cause: 'posisi',
    inactive_reason: reason,
    inactive_at: new Date().toISOString()
  })
  masterLog(
    'RM',
    PAGE,
    'mst-deactivate',
    item.code,
    `Nonaktifkan posisi ${item.code} (${whCodeOfRack(rack)}) — alasan: ${reason}`
  )
  return { code: item.code, is_active: false }
}

registerEntity('rack', {
  collection: 'mt_rack',
  entity: 'Rack',
  page: PAGE,
  wh: 'RM',
  labelOf: row => String(row.code),
  usage: rackUsage,
  deletable: true,
  guardOff: row => {
    const stocked = stockedBinIds()
    const hit = binsOf(row.id).filter(bin => bin.is_active !== false && stocked.has(String(bin.id))).length
    return hit
      ? `Masih ada stok di ${hit} posisi pada rack ini. Pindahkan dulu lewat Pindah Material, lalu coba lagi.`
      : null
  },
  setActive: (row, body) => {
    requireRm()
    const now = new Date().toISOString()
    if (body.is_active) {
      binsOf(row.id)
        .filter(bin => bin.inactive_cause === 'rack')
        .forEach(bin =>
          updateRow('mt_rack_bin', bin.id, {
            ...auditFieldsForUpdate(),
            is_active: true,
            inactive_cause: null,
            inactive_reason: null,
            inactive_at: null
          })
        )
    } else {
      binsOf(row.id)
        .filter(bin => bin.is_active !== false)
        .forEach(bin =>
          updateRow('mt_rack_bin', bin.id, {
            ...auditFieldsForUpdate(),
            is_active: false,
            inactive_cause: 'rack',
            inactive_reason: body.reason ?? null,
            inactive_at: now
          })
        )
    }
    return applyStatus(
      'mt_rack',
      { id: row.id, is_active: body.is_active, reason: body.reason },
      {
        entity: 'Rack',
        label: String(row.code),
        page: PAGE,
        wh: 'RM'
      }
    )
  },
  remove: row => {
    requireRm()
    const code = String(row.code)
    const bins = binsOf(row.id)
    bins.forEach(bin => deleteRow('mt_rack_bin', bin.id))
    deleteRow('mt_rack', row.id)
    masterLog('RM', PAGE, 'mst-delete', code, `Hapus rack ${code} (${bins.length} lokasi) — belum pernah dipakai`)
  }
})

/* ------------------------------------------------------------------ rack: import (berkas .xlsx sungguhan) */

export const RACK_IMPORT_FILE = 'Templat Import Rack RM.xlsx'
/** Judul kolom templat (sama dengan `RACK_IMPORT_COLUMNS` di service-library; dicek oleh uji). */
export const RACK_IMPORT_HEADERS = ['Kode Rack', 'Warehouse', 'Bay', 'Level', 'Posisi', 'Kategori', 'Nama Rack']
const WH_ALIAS: Record<string, string> = {
  'WH-RM': 'WH-RM',
  'WH RM': 'WH-RM',
  RM: 'WH-RM',
  'WH-SGI': 'WH-SGI',
  'WH SANSUGI': 'WH-SGI',
  SANSUGI: 'WH-SGI',
  SGI: 'WH-SGI'
}

type RackImportVerdict = ImportCheck & {
  wh?: string
  code?: string
  size?: [number, number, number]
  name?: string
  categoryId?: string | null
}

function rackImportVerdict(raw: Record<string, string>, seen: Set<string>): RackImportVerdict {
  const code = up(raw['Kode Rack'])
  if (!code) return { state: 'invalid', reason: 'Kode Rack wajib diisi' }
  if (!RACK_CODE.test(code))
    return { state: 'invalid', reason: 'Format kode salah (R + dua angka, Sansugi S + dua angka)' }
  const again = seen.has(code)
  seen.add(code)
  if (again) return { state: 'duplicate', reason: 'Kode dobel di berkas' }
  if (rackExists(code)) return { state: 'duplicate', reason: 'Sudah ada' }
  const scope = locationScope()
  const whRaw = text(raw.Warehouse)
  const wh = whRaw ? WH_ALIAS[up(whRaw)] : scope.whs[0]
  if (!wh) return { state: 'invalid', reason: `Warehouse "${whRaw}" tak dikenal (WH RM / WH Sansugi)` }
  if (!scope.whs.includes(wh)) return { state: 'invalid', reason: 'Warehouse di luar cakupan peran Anda' }
  const [bay, lv, pos] = [raw.Bay, raw.Level, raw.Posisi].map(value => Number(text(value)))
  if (!isInt(bay, 1, RACK_LIMIT.bay)) return { state: 'invalid', reason: `Bay: isi 1 sampai ${RACK_LIMIT.bay}` }
  if (!isInt(lv, 1, RACK_LIMIT.level)) return { state: 'invalid', reason: `Level: isi 1 sampai ${RACK_LIMIT.level}` }
  if (!isInt(pos, 1, RACK_LIMIT.position))
    return {
      state: 'invalid',
      reason: `Posisi: bilangan bulat mulai 1 (kode posisi dua angka, paling banyak ${RACK_LIMIT.position})`
    }
  if (bay * lv * pos > RACK_LARGE)
    return { state: 'invalid', reason: `${bay * lv * pos} lokasi melebihi ${RACK_LARGE} — buat lewat form Tambah Rack` }
  const categoryRaw = text(raw.Kategori)
  const group = categoryRaw
    ? readTable('mt_material_group').find(row => text(row.name).toLowerCase() === categoryRaw.toLowerCase())
    : undefined
  if (categoryRaw && !group) return { state: 'invalid', reason: `Kategori "${categoryRaw}" tak dikenal` }
  return {
    state: 'valid',
    wh,
    code,
    size: [bay, lv, pos],
    name: text(raw['Nama Rack']),
    categoryId: group ? String(group.id) : null
  }
}

export function rackImportPreview(body: ImportRowsBody) {
  requireRm()
  const seen = new Set<string>()
  const rows: ImportRow[] = importRowsOf(body).map(({ n, raw }) => {
    const verdict = rackImportVerdict(raw, seen)
    const size = verdict.size
      ? `${verdict.size.join(' × ')} = ${verdict.size[0] * verdict.size[1] * verdict.size[2]}`
      : '—'
    return {
      n,
      cells: [up(raw['Kode Rack']), WH_LABEL[verdict.wh ?? ''] ?? (text(raw.Warehouse) || '—'), size],
      state: verdict.state,
      reason: verdict.reason
    }
  })
  return importSummary(importFileOf(body, RACK_IMPORT_FILE), rows)
}

export function rackImportCommit(body: ImportRowsBody) {
  requireRm()
  const seen = new Set<string>()
  let imported = 0
  let skipped = 0
  let rejected = 0
  let locations = 0
  importRowsOf(body).forEach(({ raw }) => {
    const verdict = rackImportVerdict(raw, seen)
    if (verdict.state === 'duplicate') skipped += 1
    else if (verdict.state === 'invalid' || !verdict.size || !verdict.wh || !verdict.code) rejected += 1
    else {
      const [bay, lv, pos] = verdict.size
      const rack = insertRow('mt_rack', {
        ...auditFieldsForCreate(),
        id: newId(),
        code: verdict.code,
        name: verdict.name || null,
        zone_id: rackWhZone(verdict.wh).id,
        category_restriction_id: verdict.categoryId ?? null,
        is_active: true,
        inactive_reason: null
      }) as LocalRow
      for (let b = 1; b <= bay; b += 1)
        for (let l = 1; l <= lv; l += 1)
          for (let p = 1; p <= pos; p += 1) {
            insertRow('mt_rack_bin', binRecord(rack, b, l, p))
            locations += 1
          }
      imported += 1
    }
  })
  if (!imported) fail('Tidak ada baris valid untuk diimport.')
  masterLog(
    'RM',
    PAGE,
    'mst-import',
    importFileOf(body, RACK_IMPORT_FILE),
    `Import ${imported} rack (${locations} lokasi) — dilewati ${skipped} sudah ada, ditolak ${rejected}`
  )
  return { imported, skipped, rejected }
}

/* ------------------------------------------------------------------ FG: zone, pallet, container */

const zoneById = (id: unknown) => {
  const zone = findRow('warehouse_zone', text(id))
  if (!zone || zone.zone_type !== 'STORAGE_FG') fail('Zone tidak ditemukan atau sudah tidak ada.', 'ERR_NOT_FOUND', 404)
  return zone
}
const palletsOfZone = (zoneId: string) =>
  readTable('pallet')
    .filter(row => row.pallet_kind === 'FG' && idOf(row.zone_id) === zoneId)
    .sort((a, b) => palletNo(String(a.code)) - palletNo(String(b.code)))

function palletUsage(row: LocalRow): Usage {
  return usageOfCounts(referenceCounts('pallet', new Set([String(row.id)])))
}
function zoneUsage(zone: LocalRow): Usage {
  const own = referenceCounts('warehouse_zone', new Set([String(zone.id)]), ['pallet', 'container'])
  const pallets = palletsOfZone(zone.id)
  referenceCounts('pallet', new Set(pallets.map(row => String(row.id)))).forEach((count, key) =>
    own.set(key, (own.get(key) ?? 0) + count)
  )
  return usageOfCounts(own)
}
const containerUsage = (row: LocalRow): Usage => usageOfCounts(referenceCounts('container', new Set([String(row.id)])))

function validateCapacity(cap: number, rec: number) {
  if (cap < 1) fail('Kapasitas pallet minimal 1 karton.')
  if (rec < 1 || rec > cap) fail(`Rekomendasi harus antara 1 dan kapasitas (${cap}).`)
}

const newPallet = (zone: LocalRow, number: number, cap: number, rec: number): LocalRow => ({
  ...auditFieldsForCreate(),
  id: newId(),
  code: `${zone.code}-P${number}`,
  pallet_kind: 'FG',
  zone_id: zone.id,
  capacity: cap,
  recommended: rec,
  unit_type: null,
  description: `Pallet FG ${zone.code}-P${number}`,
  pallet_status: 'EMPTY',
  cycle_count: 0,
  is_active: true,
  inactive_reason: null,
  sku_id: null,
  occupied: 0
})

type ZoneSave = { id?: string; code?: string; name?: string; pallets?: unknown; cap?: unknown; rec?: unknown }

export function zoneSave(body: ZoneSave) {
  requireFg()
  const name = text(body.name)
  if (!name) fail(body.id ? 'Nama wajib diisi.' : 'Kode dan Nama wajib diisi.')
  const wanted = Number(body.pallets)
  if (body.id) {
    const zone = zoneById(body.id)
    const existing = palletsOfZone(zone.id)
    if (!Number.isInteger(wanted) || wanted < 0 || wanted > 12) fail('Jumlah pallet tidak sah (0 sampai 12).')
    if (wanted < existing.length) {
      const live = liveFg()
      const removed = existing.slice(wanted)
      const filled = removed.filter(row => !palletRow(row, live).empty)
      if (filled.length)
        fail(
          `Pallet ${filled.map(row => row.code).join(', ')} masih berisi barang — kosongkan dulu.`,
          'ERR_IN_USE',
          409
        )
      const hist = removed.filter(row => palletUsage(row).total > 0)
      if (hist.length)
        fail(
          `Pallet ${hist.map(row => row.code).join(', ')} sudah punya riwayat — nonaktifkan saja, jumlahnya tidak bisa dikurangi.`,
          'ERR_IN_USE',
          409
        )
      removed.forEach(row => deleteRow('pallet', row.id))
    }
    const cap = existing[0] ? palletCap(existing[0]) : PALLET_DEFAULT.cap
    const rec = existing[0] ? palletRec(existing[0]) : PALLET_DEFAULT.rec
    const top = existing.reduce((max, row) => Math.max(max, palletNo(String(row.code))), 0)
    for (let index = existing.length + 1; index <= wanted; index += 1)
      insertRow('pallet', newPallet(zone, top + (index - existing.length), cap, rec))
    updateRow('warehouse_zone', zone.id, { ...auditFieldsForUpdate(), name })
    masterLog(
      'FG',
      PAGE,
      'mst-edit',
      String(zone.code),
      `Ubah zone ${zone.code} — nama "${name}", ${wanted} pallet (sebelumnya ${existing.length})`
    )
    return { id: zone.id, code: String(zone.code), pallets: wanted }
  }
  const code = up(body.code)
  if (!code) fail('Kode dan Nama wajib diisi.')
  if (!ZONE_CODE.test(code)) fail('Kode zone: huruf besar atau angka, tanpa spasi, maksimal 10 karakter.')
  if (readTable('warehouse_zone').some(row => up(row.code) === code)) fail(`Kode ${code} sudah ada.`)
  if (!Number.isInteger(wanted) || wanted < 1) fail('Jumlah pallet minimal 1.')
  if (wanted > 12) fail('Jumlah pallet maksimal 12 per zone.')
  const cap = Number(body.cap)
  const rec = Number(body.rec)
  validateCapacity(cap || 0, rec || 0)
  const warehouse = readTable('mt_warehouses').find(row => row.code === 'WH-FG')
  const zone = insertRow('warehouse_zone', {
    ...auditFieldsForCreate(),
    id: newId(),
    code,
    name,
    warehouse_id: warehouse?.id ?? null,
    zone_type: 'STORAGE_FG',
    is_active: true,
    inactive_reason: null
  }) as LocalRow
  for (let index = 1; index <= wanted; index += 1) insertRow('pallet', newPallet(zone, index, cap, rec))
  masterLog('FG', PAGE, 'mst-add', code, `Tambah zone FG ${code} beserta ${wanted} pallet (maks ${cap}, saran ${rec})`)
  return { id: zone.id, code, pallets: wanted }
}

export function palletSave(body: { id?: string; cap?: unknown; rec?: unknown }) {
  requireFg()
  const row = findRow('pallet', text(body.id))
  if (!row || row.pallet_kind !== 'FG') fail('Pallet tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const cap = Number(body.cap) || 0
  const rec = Number(body.rec) || 0
  validateCapacity(cap, rec)
  const live = palletRow(row, liveFg())
  if (live.keq > cap)
    fail(`Pallet sudah berisi ${live.keq} karton — kapasitas tidak boleh di bawah isinya.`, 'ERR_IN_USE', 409)
  updateRow('pallet', row.id, { ...auditFieldsForUpdate(), capacity: cap, recommended: rec })
  masterLog('FG', PAGE, 'mst-edit', String(row.code), `Ubah pallet ${row.code} — maks ${cap}, saran ${rec}`)
  return { id: row.id, cap, rec }
}

export function palletAdd(body: { zone_id?: string; count?: unknown; cap?: unknown; rec?: unknown }) {
  requireFg()
  const zone = zoneById(body.zone_id)
  const count = Number(body.count)
  if (!Number.isInteger(count) || count < 1) fail('Jumlah palletnya belum diisi.')
  if (count > PALLET_MAX_NEW) fail(`Jumlah pallet baru maksimal ${PALLET_MAX_NEW}.`)
  const cap = Number(body.cap) || 0
  const rec = Number(body.rec) || 0
  if (cap < 1 || rec < 1) fail('Kapasitas dan rekomendasi harus lebih dari nol.')
  if (rec > cap) fail('Rekomendasi tidak boleh melebihi kapasitas.')
  const top = palletsOfZone(zone.id).reduce((max, row) => Math.max(max, palletNo(String(row.code))), 0)
  const codes: string[] = []
  for (let index = 1; index <= count; index += 1) {
    insertRow('pallet', newPallet(zone, top + index, cap, rec))
    codes.push(`${zone.code}-P${top + index}`)
  }
  masterLog(
    'FG',
    PAGE,
    'mst-create',
    String(zone.code),
    `Tambah ${count} pallet di zone ${zone.code} (${codes.join(', ')}) — maks ${cap}, saran ${rec}`
  )
  return { zone: String(zone.code), codes }
}

export function containerSave(body: { id?: string; code?: string; name?: string; kemasan?: string }) {
  requireFg()
  const name = text(body.name)
  const kemasan = text(body.kemasan)
  if (!['Slop', 'Pack'].includes(kemasan)) fail('Pilih jenis kemasan Slop atau Pack lepas.')
  if (body.id) {
    const row = findRow('container', text(body.id))
    if (!row) fail('Container tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    if (!name) fail('Nama wajib diisi.')
    const stock = liveFg().containers.get(String(row.code))
    const filled = (stock?.slop ?? 0) > 0 || (stock?.pack ?? 0) > 0
    if (filled && kemasan !== row.container_type)
      fail(
        'Jenis kemasan terkunci karena container ini sedang berisi barang. Kosongkan dulu bila memang harus diganti.'
      )
    updateRow('container', row.id, { ...auditFieldsForUpdate(), name, container_type: kemasan })
    masterLog('FG', PAGE, 'mst-edit', String(row.code), `Ubah container ${row.code} — "${name}", khusus ${kemasan}`)
    return { id: row.id, code: String(row.code) }
  }
  const code = up(body.code)
  if (!code || !name) fail('Kode dan Nama wajib diisi.')
  if (readTable('container').some(row => up(row.code) === code)) fail(`Kode ${code} sudah ada.`)
  const row = insertRow('container', {
    ...auditFieldsForCreate(),
    id: newId(),
    code,
    name,
    container_type: kemasan,
    zone_id: null,
    is_active: true,
    inactive_reason: null
  }) as LocalRow
  masterLog('FG', PAGE, 'mst-add', code, `Tambah container FG ${code} (${kemasan})`)
  return { id: row.id, code }
}

registerEntity('fgzone', {
  collection: 'warehouse_zone',
  entity: 'Zone',
  page: PAGE,
  wh: 'FG',
  labelOf: row => String(row.code),
  usage: zoneUsage,
  deletable: true,
  setActive: (row, body) => {
    requireFg()
    return applyStatus(
      'warehouse_zone',
      { id: row.id, ...body },
      {
        entity: 'Zone',
        label: String(row.code),
        page: PAGE,
        wh: 'FG'
      }
    )
  },
  remove: row => {
    requireFg()
    palletsOfZone(row.id).forEach(pallet => deleteRow('pallet', pallet.id))
    deleteRow('warehouse_zone', row.id)
    masterLog('FG', PAGE, 'mst-delete', String(row.code), `Hapus zone ${row.code} — belum terpakai`)
  }
})

registerEntity('fgpallet', {
  collection: 'pallet',
  entity: 'Pallet',
  page: PAGE,
  wh: 'FG',
  labelOf: row => String(row.code),
  usage: palletUsage,
  deletable: true,
  setActive: (row, body) => {
    requireFg()
    return applyStatus(
      'pallet',
      { id: row.id, ...body },
      {
        entity: 'Pallet',
        label: String(row.code),
        page: PAGE,
        wh: 'FG'
      }
    )
  },
  remove: row => {
    requireFg()
    if (!palletRow(row, liveFg()).empty) fail('Pallet masih berisi barang — kosongkan dulu.', 'ERR_IN_USE', 409)
    deleteRow('pallet', row.id)
    masterLog('FG', PAGE, 'mst-delete', String(row.code), `Hapus pallet ${row.code} — belum terpakai`)
  }
})

registerEntity('container', {
  collection: 'container',
  entity: 'Container',
  page: PAGE,
  wh: 'FG',
  labelOf: row => String(row.code),
  usage: containerUsage,
  deletable: true,
  setActive: (row, body) => {
    requireFg()
    return applyStatus(
      'container',
      { id: row.id, ...body },
      {
        entity: 'Container',
        label: String(row.code),
        page: PAGE,
        wh: 'FG'
      }
    )
  },
  remove: row => {
    requireFg()
    deleteRow('container', row.id)
    masterLog('FG', PAGE, 'mst-delete', String(row.code), `Hapus container ${row.code} — kosong dan belum terpakai`)
  }
})

/* ------------------------------------------------------------------ cetak label (jejak) */

export function printLog(body: { kind?: string; ref?: string; count?: number; detail?: string }) {
  const kind = text(body.kind)
  const wh = kind === 'rack' || kind === 'position' ? 'RM' : 'FG'
  masterLog(wh, PAGE, 'mst-print-label', text(body.ref), text(body.detail) || `Cetak ${num(body.count)} label lokasi`)
  return { ok: true }
}

/* ------------------------------------------------------------------ register */

export function registerMasterLocationHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/master/lokasi'
  const get = (path: string, run: (params?: Record<string, unknown>) => unknown) =>
    mock.onGet(`${base}/${path}`).reply(config => handle(() => run(config.params)))
  const post = (path: string, run: (body: never) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse(config.data) as never)))
  get('list', () => lokasiList())
  get('rack/detail', params => rackDetail(params as { id?: string }))
  post('rack/edit-structure', rackEditStructure)
  post('rack/position-status', positionStatus)
  post('rack/import/preview', rackImportPreview as (body: never) => unknown)
  post('rack/import/commit', rackImportCommit as (body: never) => unknown)
  post('zone/save', zoneSave)
  post('pallet/save', palletSave)
  post('pallet/add', palletAdd)
  post('container/save', containerSave)
  post('print', printLog)
}
